import { type Tournament } from '@clubspark-node/clubspark-tennis-types';
import { toast } from 'react-toastify';
import { competitionEngine } from 'tods-competition-factory';

import { type COMPETITION_ENGINE, type TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { TournamentRepositoryImpl } from 'repository/grapqhlRepository/tournamentRepository';
import { type CloudResult, type RepositoryCacheType } from 'repository/repository';
import type TournamentService from 'services/tournament/tournamentService';
import { displayErrorMessage } from 'services/utils';
import { type DraftTournamentInterface } from 'typedefs/api/graphql/tournamentTypes';
import { type EventCost } from 'typedefs/event';
import {
  type DraftTournamentsPaginatedVariables,
  type LinkableTournament,
  type SaveTournamentResult,
  type TournamentRolesResponseInterface,
  type TournamentUrlsResponse,
} from 'typedefs/tournamentTypes';
import { isStandalone } from 'utils/environment';

export default class TournamentServiceImpl implements TournamentService {
  tournamentRepository = new TournamentRepositoryImpl();

  /**
   * @param id {string} tournament id
   * @param cache {RepositoryCacheType} cache settings to use
   * @param showNotification {boolean} display toaster notification
   */
  async get(id: string, cache?: RepositoryCacheType, showNotification = true): Promise<CloudResult<Tournament>> {
    const tournament = await this.tournamentRepository.get(id, cache);
    if (showNotification) {
      displayErrorMessage<Tournament>(tournament);
    }
    return tournament;
  }

  async syncTournament(id: string): Promise<CloudResult<Tournament>> {
    const result = await this.tournamentRepository.syncTournament(id);
    displayErrorMessage<Tournament>(result);
    return result;
  }

  async getDraftTournaments(
    paginationOptions: DraftTournamentsPaginatedVariables,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<{ draftTournaments: { items: DraftTournamentInterface[]; totalItems?: number } }>> {
    const draftTournamentsResult = await this.tournamentRepository.getDraftTournaments(paginationOptions, cache);
    if (draftTournamentsResult.error) {
      return Promise.reject(draftTournamentsResult.error);
    }
    const tournamentWithMappedNames = draftTournamentsResult?.data?.draftTournaments?.items?.map((tournament) => ({
      ...tournament,
      tournamentName: tournament.tournamentName || tournament.name, // tournament.tournamentName is for demo mode
    }));
    return Promise.resolve({
      data: {
        draftTournaments: {
          items: tournamentWithMappedNames,
          totalItems: draftTournamentsResult.data.draftTournaments.totalItems || tournamentWithMappedNames?.length,
        },
      },
    });
  }

  async getLinkableTournaments(id: string, cache?: RepositoryCacheType): Promise<CloudResult<LinkableTournament[]>> {
    const linkableTournaments = await this.tournamentRepository.getLinkableTournaments(id, cache);

    // show toaster with error message if it exists
    displayErrorMessage<LinkableTournament[]>(linkableTournaments);

    return Promise.resolve(linkableTournaments);
  }

  /**
   * @param ids {string[]} array of tournaments ids
   * @param cache {RepositoryCacheType} cache settings to use
   */
  getTournaments(ids: string[], cache?: RepositoryCacheType): Promise<CloudResult<Tournament>[]> {
    return Promise.all(ids.map(async (id) => this.get(id, cache)));
  }

  // TODO: check with someone what are tournamentUrls - by
  async getTournamentUrls(id: string, cache?: RepositoryCacheType): Promise<CloudResult<TournamentUrlsResponse>> {
    return this.tournamentRepository.getTournamentUrls(id, cache);
  }

  getTournamentMutationErrorMessage(error) {
    if (error?.code === 'E001') {
      return `${error.message} Press OK to reload the data.`;
    } else if (error.message) {
      return error.message;
    } else {
      return 'Ooops... Something went wrong';
    }
  }

  async saveTournament(
    id: string,
    engine: typeof TOURNAMENT_ENGINE | typeof COMPETITION_ENGINE,
    methods: { method: string; params: Record<string, any> }[],
  ): Promise<CloudResult<SaveTournamentResult>> {
    const result = await this.tournamentRepository.saveTournament(id, engine, methods);
    if (result.error) {
      const error = result?.data?.mutateTournament?.error;
      const message = this.getTournamentMutationErrorMessage(error);
      // eslint-disable-next-line no-console
      if (Object.keys(window).includes('dev')) console.log({ error });
      toast.error(message);
    } else if (result?.data && isStandalone()) {
      // save all tournaments to indexed db
      const tournamentRecordsObjects = competitionEngine.getState().tournamentRecords;
      const tournamentRecords = Object.values(tournamentRecordsObjects);
      const { save } = await import('services/storage/db');
      tournamentRecords.forEach((tournament) => save.local({ tournament }));
    }
    return Promise.resolve(result);
  }

  async getEventsCosts(
    eventIds: string[],
    personCsIds: string[],
    tournamentId: string,
    cache?: RepositoryCacheType,
    // eslint-disable-next-line no-undef
  ): Promise<CloudResult<EventCost[]>> {
    const eventCostResult = await this.tournamentRepository.getEventsCosts(eventIds, personCsIds, tournamentId, cache);

    // show toaster with error message if it exists
    displayErrorMessage<EventCost[]>(eventCostResult);

    return Promise.resolve(eventCostResult);
  }

  async getTournamentRoles(
    id: string,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<TournamentRolesResponseInterface>> {
    return this.tournamentRepository.getTournamentRoles(id, cache);
  }
}
